import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Navigation, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { api } from "../../lib/api";

const ROLE_LABELS = { test_drive: "Test Drive", resell: "Vendita", service: "Service" };
const FILTERS = [
  { key: "", label: "Tutti", testid: "network-filter-all" },
  { key: "test_drive", label: "Test Drive", testid: "network-filter-testdrive" },
  { key: "resell", label: "Vendita", testid: "network-filter-resell" },
  { key: "service", label: "Service", testid: "network-filter-service" },
];

const markerIcon = L.divIcon({
  className: "",
  html: '<div style="width:14px;height:14px;background:#FFD700;transform:rotate(45deg);border:2px solid #0A0A0B;box-shadow:0 0 0 3px rgba(255,215,0,0.35)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function FlyTo({ centre }) {
  const map = useMap();
  useEffect(() => {
    if (centre) map.flyTo([centre.lat, centre.lng], 11, { duration: 1.2 });
  }, [centre, map]);
  return null;
}

export default function NetworkMap({ compact = false }) {
  const [centres, setCentres] = useState([]);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (role) params.set("role", role);
      api.get(`/centres?${params}`).then((r) => {
        setCentres(r.data);
        if (r.data.length && selected && !r.data.find((c) => c.id === selected.id)) setSelected(null);
      }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, role]);

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      let best = null;
      let bestD = Infinity;
      centres.forEach((c) => {
        const d = (c.lat - latitude) ** 2 + (c.lng - longitude) ** 2;
        if (d < bestD) { bestD = d; best = c; }
      });
      if (best) setSelected(best);
    });
  };

  const mapCentres = useMemo(() => centres.filter((c) => c.lat && c.lng), [centres]);

  return (
    <div data-testid="network-map" className="grid gap-8 lg:grid-cols-[1fr_1.6fr]">
      <div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={f.testid}
              onClick={() => setRole(f.key)}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                role === f.key ? "border-swapa-yellow text-swapa-yellow" : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          data-testid="network-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per CAP, città o centro"
          className="mt-4 w-full border border-white/15 bg-ink px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none"
        />
        <button
          data-testid="network-geolocate-button"
          onClick={locate}
          className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-swapa-yellow"
        >
          <Navigation className="h-3.5 w-3.5" /> Usa la mia posizione (con consenso)
        </button>

        <ul className={`mt-6 space-y-3 overflow-y-auto pr-1 ${compact ? "max-h-[320px]" : "max-h-[420px]"}`} data-testid="network-centre-list">
          {centres.map((c) => (
            <li key={c.id}>
              <button
                data-testid={`network-centre-${c.id}`}
                onClick={() => setSelected(c)}
                className={`w-full border p-4 text-left transition-colors duration-300 ${
                  selected?.id === c.id ? "border-swapa-yellow bg-white/5" : "border-white/10 hover:border-white/30"
                }`}
              >
                <p className="font-display text-base font-bold uppercase tracking-tight text-white">{c.name}</p>
                {c.host && (
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">presso {c.host}</p>
                )}
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  {c.address} — {c.cap} {c.city} ({c.province})
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.roles.map((r) => (
                    <span key={r} className="border border-white/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">
                      {ROLE_LABELS[r] || r}
                    </span>
                  ))}
                </div>
              </button>
            </li>
          ))}
          {centres.length === 0 && (
            <li className="border border-white/10 p-6 text-sm text-zinc-500" data-testid="network-empty-state">
              Nessun centro trovato per questa ricerca. Prova con un'altra città o CAP.
            </li>
          )}
        </ul>
      </div>

      <div className="relative min-h-[380px] overflow-hidden border border-white/10 lg:min-h-[520px]">
        <MapContainer center={[42.4, 12.5]} zoom={5.6} scrollWheelZoom={false} className="absolute inset-0 h-full w-full">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="map-tiles-dark"
          />
          <FlyTo centre={selected} />
          {mapCentres.map((c) => (
            <Marker key={c.id} position={[c.lat, c.lng]} icon={markerIcon} eventHandlers={{ click: () => setSelected(c) }}>
              <Popup>
                <p className="font-bold uppercase">{c.name}</p>
                <p className="mt-1 text-zinc-400">{c.address}, {c.cap} {c.city}</p>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {selected && (
          <div data-testid="network-centre-panel" className="absolute bottom-0 left-0 right-0 z-[500] border-t border-swapa-yellow/40 bg-ink/95 p-5 backdrop-blur-xl sm:left-5 sm:right-auto sm:bottom-5 sm:w-[360px] sm:border sm:border-white/15">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-swapa-yellow">Centro selezionato</p>
            <p className="mt-2 font-display text-xl font-bold uppercase tracking-tight text-white">{selected.name}</p>
            {selected.host && (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-300">presso {selected.host}</p>
            )}
            <p className="mt-1 text-sm text-zinc-400">{selected.address} — {selected.cap} {selected.city} ({selected.province})</p>
            <div className="mt-3 space-y-1.5 text-xs text-zinc-400">
              {selected.hours && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-zinc-500" />{selected.hours}</p>}
              {selected.phone && <a href={`tel:${selected.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-3.5 w-3.5 text-zinc-500" />{selected.phone}</a>}
              {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="h-3.5 w-3.5 text-zinc-500" />{selected.email}</a>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {selected.roles.includes("test_drive") && (
                <Link
                  to={`/test-drive?centro=${selected.id}`}
                  data-testid="network-book-testdrive-cta"
                  className="inline-flex items-center gap-2 bg-swapa-yellow px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-black"
                >
                  Prenota test drive <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              )}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="network-directions-cta"
                className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white hover:border-swapa-yellow"
              >
                Indicazioni
              </a>
              {selected.email && (
                <a
                  href={`mailto:${selected.email}`}
                  data-testid="network-contact-cta"
                  className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white hover:border-swapa-yellow"
                >
                  Contatta
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
